import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Container,
    Form,
    Image,
    InputGroup,
    Row,
    Stack,
} from 'react-bootstrap';

import BgImage from '../../Assets/Img/login_bg.png';
import Logo from '../../Assets/Img/logo.svg';

import './login.scss';

import {
    useLocation,
    useNavigate,
    useNavigationType,
} from 'react-router';

import LoadingComponent from '../LoadingComponent';

import {
    Eye,
    EyeOff,
} from 'lucide-react';

import {
    useGetprofileMutation,
    useGetTokenMutation,
    useLoggedInUserValidateMutation,
    useLoggedInOtpVerifyMutation,
    useEmailValidateMutation,
    useOtpVerifyMutation,
} from './loginApi';

import { useTranslation } from 'react-i18next';

import {
    setSecureItem,
    getSecureItem
} from "../../utils/secureStorage";

const Login = () => {

    const { i18n } = useTranslation();

    const navigate = useNavigate();

    const navigationType = useNavigationType();

    const location = useLocation();

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState(false);

    const [loginConfig, setLoginConfig] = useState({
        email: '',
        password: '',
    });

    const [showPassword, setShowPassword] = useState(false);

    const [apiError, setApiError] = useState("");

    const [step, setStep] = useState("login");

    const [emailVerify, setEmailVerify] = useState("");

    const [otp, setOtp] = useState("");

    const [timer, setTimer] = useState(60);

    const [canResend, setCanResend] = useState(false);

    const [otpExpired, setOtpExpired] = useState(false);

    const [loginType, setLoginType] = useState("");

    const [identifierSubmitted, setIdentifierSubmitted] = useState(false);

    const [getToken] = useGetTokenMutation();

    const [getProfile] = useGetprofileMutation();

    const [loggedInUserValidate] =
        useLoggedInUserValidateMutation();

    const [loggedInOtpVerify] =
        useLoggedInOtpVerifyMutation();

    const [emailValidate] =
        useEmailValidateMutation();

    const [otpVerify] =
        useOtpVerifyMutation();

    const token =
        location.search.replace("?token=", "");

    // REDIRECT OLD DOMAIN
    useEffect(() => {

        const currentHostname =
            window.location.hostname;

        const currentPath =
            window.location.pathname;

        if (
            currentHostname ===
            'apps.symcarelab.com' ||

            currentHostname ===
            'app.symvisionlab.com'
        ) {

            const newUrl =
                `https://app.symcarelab.com${currentPath}${window.location.search}${window.location.hash}`;

            window.location.replace(newUrl);
        }

    }, []);

    // INITIAL LOAD
    useEffect(() => {

        (async () => {

            if (location.search !== '') {

                setSecureItem(
                    "authToken",
                    token
                );

                const userData =
                    await getProfile().unwrap();

                setSecureItem(
                    'UserData',
                    userData
                );

                navigate('/dashboard');

                return;
            }

            i18n.changeLanguage('fr');

            const tokenStored =
                getSecureItem('authToken');

            const loginStep =
                getSecureItem('loginStep');

            const storedLoginType =
                getSecureItem('loginType');

            if (
                tokenStored &&
                !loginStep &&
                navigationType !== "POP"
            ) {

                navigate('/dashboard');
            }

            if (loginStep) {

                setStep(loginStep);

                // RESTORE LOGIN TYPE
                if (storedLoginType) {

                    setLoginType(storedLoginType);

                    if (
                        storedLoginType === "mobile"
                    ) {

                        setIdentifierSubmitted(true);
                    }
                }
            }

        })();

    }, []);

    // PREVENT BACK
    useEffect(() => {

        const handleBack = () => {

            const loginStep =
                getSecureItem("loginStep");

            if (loginStep) {

                window.history.pushState(
                    null,
                    "",
                    window.location.href
                );
            }
        };

        window.addEventListener(
            "popstate",
            handleBack
        );

        return () =>
            window.removeEventListener(
                "popstate",
                handleBack
            );

    }, []);

    // OTP TIMER
    useEffect(() => {

        if (
            step === "otp" &&
            timer > 0
        ) {

            const interval =
                setInterval(() => {

                    setTimer((prev) => prev - 1);

                }, 1000);

            return () => clearInterval(interval);

        } else if (timer === 0) {

            setCanResend(true);
        }

    }, [timer, step]);

    // OTP EXPIRY
    useEffect(() => {

        if (step === "otp") {

            const expiry =
                setTimeout(() => {

                    setOtpExpired(true);

                }, 120000);

            return () => clearTimeout(expiry);
        }

    }, [step]);

    // PASSWORD TOGGLE
    const togglePasswordVisibility = () => {

        setShowPassword(!showPassword);
    };

    // INPUT CHANGE
    const changeHandle = (e) => {

        setLoginConfig((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));

        // RESET LOGIN FLOW IF USER CHANGES IDENTIFIER
        if (e.target.name === "email") {

            setIdentifierSubmitted(false);

            setLoginType("");

            setApiError("");

            setOtp("");

            setStep("login");

            setSecureItem(
                "loginType",
                null
            );

            setSecureItem(
                "loginStep",
                null
            );
        }
    };

    /*
    ============================================================
    TEMPORARILY DISABLED MOBILE / IDENTIFIER-FIRST LOGIN FLOW
    ============================================================

    The original code is retained here and has NOT been deleted.

    It can be enabled again later when mobile-number login
    is required.

    // VALIDATE LOGIN TYPE
    const handleIdentifierSubmit = async () => {

        if (!loginConfig.email) {

            setApiError(
                "Please enter Email, User ID or Mobile Number"
            );

            return;
        }

        setLoading(true);

        setApiError("");

        try {

            const result =
                await loggedInUserValidate({
                    loggedInType:
                        loginConfig.email
                });

            const response = result?.data;

            // INVALID USER
            if (!response?.success) {

                setApiError(
                    response?.message ||
                    "Invalid login identifier"
                );

                setIdentifierSubmitted(false);

                setLoginType("");

                return;
            }

            // MOBILE LOGIN
            if (response?.isMobile) {

                setLoginType("mobile");

                setSecureItem(
                    "loginType",
                    "mobile"
                );

                setIdentifierSubmitted(true);

                if (response?.isNewuser) {

                    setApiError(
                        "New mobile user detected. OTP verification required."
                    );
                }

                setStep("otp");

                setSecureItem(
                    "loginStep",
                    "otp"
                );

                setTimer(60);

                setCanResend(false);

                setOtpExpired(false);

                return;
            }

            // PASSWORD LOGIN
            setLoginType("password");

            setSecureItem(
                "loginType",
                "password"
            );

            setIdentifierSubmitted(true);

        } catch (err) {

            setApiError(
                err?.data?.message ||
                err?.error ||
                "Something went wrong"
            );

            setIdentifierSubmitted(false);

            setLoginType("");

        } finally {

            setLoading(false);
        }
    };

    ============================================================
    END TEMPORARILY DISABLED MOBILE / IDENTIFIER-FIRST LOGIN FLOW
    ============================================================
    */


    // PASSWORD LOGIN
    // Email/User ID and password are submitted together.
    const submitHandle = async () => {

        /*
        ============================================================
        MOBILE LOGIN TEMPORARILY DISABLED
        ============================================================

        The original mobile condition is retained.

        if (loginType === "mobile") {
            return;
        }

        ============================================================
        */

        try {

            setLoading(true);

            setError(false);

            setApiError("");

            // Email/User ID + Password are sent together
            const { data } =
                await getToken(loginConfig);

            if (
                data?.response ===
                'Wrong credentials' ||

                !data?.token
            ) {

                setApiError(
                    "Invalid email/userid or password"
                );

                return;
            }

            setSecureItem(
                'authToken',
                data.token
            );

            const userData =
                await getProfile().unwrap();

            setSecureItem(
                'UserData',
                userData
            );

            // EMAIL OTP FLOW
            if (data.is2fa === true) {

                setEmailVerify(
                    userData.secondaryEmail || ""
                );

                setStep("email");

                setSecureItem(
                    "loginStep",
                    "email"
                );

                window.history.pushState(
                    null,
                    "",
                    window.location.href
                );

            } else {

                setSecureItem(
                    "loginStep",
                    null
                );

                setSecureItem(
                    "loginType",
                    null
                );

                navigate('/dashboard');
            }

        } catch (err) {

            setApiError(
                err?.data?.message ||
                "Invalid login credentials"
            );

        } finally {

            setLoading(false);
        }
    };


    // EMAIL VERIFY
    const handleEmailSubmit = async () => {

        setLoading(true);

        setApiError("");

        try {

            let result =
                await emailValidate({
                    email: emailVerify
                });

            if (result?.data?.success) {

                setStep("otp");

                setSecureItem(
                    "loginStep",
                    "otp"
                );

                setTimer(60);

                setCanResend(false);

                setOtpExpired(false);

            } else {

                setApiError(
                    result?.data?.message ||
                    "Email validation failed"
                );
            }

        } catch (err) {

            setApiError(
                "Something went wrong. Please try again."
            );
        }

        setLoading(false);
    };


    // RESEND OTP
    const handleResendOtp = async () => {

        setTimer(60);

        setCanResend(false);

        setOtpExpired(false);

        /*
        ============================================================
        MOBILE OTP RESEND TEMPORARILY DISABLED
        ============================================================

        The original mobile resend code is retained.

        if (loginType === "mobile") {

            await loggedInUserValidate({
                loggedInType:
                    loginConfig.email
            });

        }

        ============================================================
        */

        // Active flow: Email OTP
        await emailValidate({
            email: emailVerify
        });
    };


    // OTP INPUT
    const handleOtpChange = (e, index) => {

        const value =
            e.target.value.replace(/\D/, '');

        if (!value) return;

        let newOtp = otp.split('');

        newOtp[index] = value;

        const updated = newOtp.join('');

        setOtp(updated);

        if (e.target.nextSibling) {
            e.target.nextSibling.focus();
        }
    };


    // OTP BACKSPACE
    const handleOtpKeyDown = (e, index) => {

        if (e.key === "Backspace") {

            let newOtp = otp.split('');

            newOtp[index] = '';

            setOtp(newOtp.join(''));

            if (e.target.previousSibling) {
                e.target.previousSibling.focus();
            }
        }
    };


    // OTP VERIFY
    const handleOtpSubmit = async () => {

        if (otpExpired) {

            setApiError(
                "OTP expired. Please resend."
            );

            return;
        }

        setLoading(true);

        setApiError("");

        try {

            /*
            ========================================================
            MOBILE OTP VERIFY TEMPORARILY DISABLED
            ========================================================

            Original mobile OTP code retained below.

            // MOBILE OTP VERIFY
            if (loginType === "mobile") {

                const result =
                    await loggedInOtpVerify({
                        mobileNumber:
                            loginConfig.email,
                        otp: otp,
                        devicetoken: "web"
                    });

                const response =
                    result?.data;

                if (
                    response?.response !== "SUCCESS" ||
                    !response?.token
                ) {

                    setApiError(
                        response?.message ||
                        "Invalid OTP"
                    );

                    return;
                }

                // SAVE TOKEN
                setSecureItem(
                    "authToken",
                    response.token
                );

                const userData =
                    await getProfile().unwrap();

                setSecureItem(
                    "UserData",
                    userData
                );

                setSecureItem(
                    "loginStep",
                    null
                );

                setSecureItem(
                    "loginType",
                    null
                );

                navigate("/dashboard");

                return;
            }

            ========================================================
            END MOBILE OTP VERIFY
            ========================================================
            */


            // EMAIL OTP FLOW
            let result =
                await otpVerify({ otp });

            if (result?.data?.success) {

                setSecureItem(
                    "loginStep",
                    null
                );

                setSecureItem(
                    "loginType",
                    null
                );

                navigate('/dashboard');

            } else {

                setApiError(
                    result?.data?.message ||
                    "Invalid OTP"
                );

                setTimeout(() => {

                    setStep("login");

                    setSecureItem(
                        "loginStep",
                        null
                    );

                    setSecureItem(
                        "loginType",
                        null
                    );

                    navigate("/");

                }, 1500);
            }

        } catch (err) {

            setApiError(
                err?.data?.message ||
                "OTP verification failed"
            );

        } finally {

            setLoading(false);
        }
    };


    // OTP PASTE
    const handleOtpPaste = (e) => {

        e.preventDefault();

        const pasteData =
            e.clipboardData
                .getData("text")
                .trim();

        /*
        Mobile 5-digit OTP is retained for future use.

        const regex =
            loginType === "mobile"
                ? /^\d{5}$/
                : /^\d{6}$/;
        */

        // CURRENT ACTIVE FLOW: EMAIL OTP
        const regex = /^\d{6}$/;

        if (!regex.test(pasteData)) {
            return;
        }

        setOtp(pasteData);

        const inputs =
            e.target.parentNode
                .querySelectorAll("input");

        pasteData
            .split("")
            .forEach((char, index) => {

                if (inputs[index]) {
                    inputs[index].value = char;
                }
            });

        inputs[otpLength - 1]?.focus();
    };


    // BACK TO LOGIN
    const handleBackToLogin = () => {

        setStep("login");

        setSecureItem(
            "loginStep",
            null
        );

        setSecureItem(
            "loginType",
            null
        );

        setIdentifierSubmitted(false);

        setLoginType("");

        setOtp("");

        setApiError("");

        setLoginConfig({
            email: '',
            password: '',
        });

        navigate("/");
    };


    /*
    Mobile OTP length retained for future use.

    const otpLength =
        loginType === "mobile"
            ? 5
            : 6;
    */

    // CURRENT ACTIVE FLOW: EMAIL OTP = 6 DIGITS
    const otpLength = 6;


    return (

        <Container
            fluid
            className="login_wrapper"
        >

            <Row>

                <Col
                    lg={8}
                    style={{
                        backgroundImage:
                            `url(${BgImage})`
                    }}
                    className="login_left_wrapper"
                />

                <Col
                    lg={4}
                    className="login_right_wrapper"
                >

                    <div className="login_right_wrapper_inner">

                        <Stack gap={3}>

                            {/* LOGO */}
                            <div className="login_logo">
                                <Image src={Logo} />
                            </div>


                            {/* LOGIN */}
                            {step === "login" && (

                                <Form
                                    onSubmit={(e) => {

                                        e.preventDefault();

                                        /*
                                        Email/User ID and Password are
                                        submitted together.

                                        Mobile-number login is temporarily
                                        disabled.
                                        */

                                        submitHandle();
                                    }}
                                >

                                    <h3>
                                        Accédez à votre espace
                                    </h3>


                                    {/* IDENTIFIER */}
                                    <Form.Group className="mb-4">

                                        <Form.Label>
                                            Identifiant / Email
                                        </Form.Label>

                                        <Form.Control
                                            type="text"
                                            name="email"
                                            value={
                                                loginConfig.email
                                            }
                                            onChange={
                                                changeHandle
                                            }
                                            required
                                            placeholder="Votre identifiant ou email"
                                        />

                                    </Form.Group>


                                    {/*
                                    ==================================================
                                    MOBILE / IDENTIFIER-FIRST UI TEMPORARILY DISABLED
                                    ==================================================

                                    The original Change Email / Mobile UI is retained.

                                    <div className="mb-3 text-end">

                                        <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => {

                                                setIdentifierSubmitted(false);

                                                setLoginType("");

                                                setApiError("");

                                                setOtp("");

                                                setStep("login");

                                                setSecureItem(
                                                    "loginType",
                                                    null
                                                );

                                                setSecureItem(
                                                    "loginStep",
                                                    null
                                                );
                                            }}
                                        >
                                            Change Email / Mobile
                                        </Button>

                                    </div>

                                    ==================================================
                                    END MOBILE / IDENTIFIER-FIRST UI
                                    ==================================================
                                    */}


                                    {/* PASSWORD */}
                                    {/*
                                    IMPORTANT:
                                    Password is now always displayed together
                                    with Email/User ID.
                                    */}

                                    <Form.Group className="mb-4">

                                        <Form.Label>
                                            Mot de passe
                                        </Form.Label>

                                        <InputGroup>

                                            <Form.Control
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                name="password"
                                                value={
                                                    loginConfig.password
                                                }
                                                onChange={
                                                    changeHandle
                                                }
                                                required
                                                placeholder="Votre mot de passe"
                                            />

                                            <InputGroup.Text
                                                onClick={
                                                    togglePasswordVisibility
                                                }
                                            >
                                                {showPassword
                                                    ? <Eye />
                                                    : <EyeOff />}
                                            </InputGroup.Text>

                                        </InputGroup>

                                    </Form.Group>


                                    {/*
                                    ORIGINAL PASSWORD CONDITION
                                    RETAINED FOR FUTURE MOBILE LOGIN:

                                    {identifierSubmitted &&
                                        loginType !== "mobile" && (

                                            <Form.Group className="mb-4">

                                                <Form.Label>
                                                    Mot de passe
                                                </Form.Label>

                                                <InputGroup>

                                                    <Form.Control
                                                        type={
                                                            showPassword
                                                                ? 'text'
                                                                : 'password'
                                                        }
                                                        name="password"
                                                        value={
                                                            loginConfig.password
                                                        }
                                                        onChange={
                                                            changeHandle
                                                        }
                                                        required
                                                        placeholder="Votre mot de passe"
                                                    />

                                                    <InputGroup.Text
                                                        onClick={
                                                            togglePasswordVisibility
                                                        }
                                                    >
                                                        {showPassword
                                                            ? <Eye />
                                                            : <EyeOff />}
                                                    </InputGroup.Text>

                                                </InputGroup>

                                            </Form.Group>
                                        )}

                                    END ORIGINAL PASSWORD CONDITION
                                    */}


                                    {/* FORGOT PASSWORD */}
                                    <div className="forgot_password">

                                        <Button
                                            variant="link"
                                            onClick={() =>
                                                navigate(
                                                    '/forgot-password'
                                                )
                                            }
                                        >
                                            Mot de passe oublié ?
                                        </Button>

                                    </div>


                                    {/* SUBMIT */}
                                    <Button
                                        type="submit"
                                        className="w-100"
                                    >
                                        Connexion
                                    </Button>


                                    {/* ERROR */}
                                    {error && (

                                        <Alert
                                            variant="danger"
                                            className="mt-3"
                                        >
                                            Merci d'entrer votre adresse mail et le mot de passe correspondant
                                        </Alert>
                                    )}


                                    {/* API ERROR */}
                                    {apiError && (

                                        <Alert
                                            variant="danger"
                                            className="mt-3 text-center"
                                        >
                                            {apiError}
                                        </Alert>
                                    )}

                                </Form>
                            )}


                            {/* EMAIL VERIFY */}
                            {step === "email" && (

                                <>

                                    <h3>
                                        Vérification de l'adresse e-mail
                                    </h3>

                                    <Form.Control
                                        type="email"
                                        value={emailVerify}
                                        onChange={(e) =>
                                            setEmailVerify(
                                                e.target.value
                                            )
                                        }
                                    />

                                    <Button
                                        className="w-100 mt-3"
                                        onClick={handleEmailSubmit}
                                    >
                                        Vérifier l'adresse e-mail
                                    </Button>

                                    {apiError && (

                                        <Alert
                                            variant="danger"
                                            className="mt-3 text-center"
                                        >
                                            {apiError}
                                        </Alert>
                                    )}

                                    <Button
                                        variant="link"
                                        className="back_button"
                                        onClick={
                                            handleBackToLogin
                                        }
                                    >
                                        ← Retour à la connexion
                                    </Button>

                                </>
                            )}


                            {/* OTP */}
                            {step === "otp" && (

                                <>

                                    <h3>
                                        Vérification OTP
                                    </h3>

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "8px"
                                        }}
                                    >

                                        {[...Array(otpLength)].map((_, i) => (

                                            <Form.Control
                                                key={i}
                                                type="text"
                                                maxLength={1}
                                                value={otp[i] || ""}
                                                onChange={(e) =>
                                                    handleOtpChange(
                                                        e,
                                                        i
                                                    )
                                                }
                                                onKeyDown={(e) =>
                                                    handleOtpKeyDown(
                                                        e,
                                                        i
                                                    )
                                                }
                                                onPaste={
                                                    handleOtpPaste
                                                }
                                                style={{
                                                    textAlign:
                                                        "center"
                                                }}
                                            />
                                        ))}

                                    </div>


                                    {/* OTP EXPIRED */}
                                    {otpExpired && (

                                        <div
                                            style={{
                                                color: "red",
                                                marginTop: "5px"
                                            }}
                                        >
                                            Le code OTP a expiré.
                                            Veuillez le renvoyer.
                                        </div>
                                    )}


                                    {/* VERIFY BUTTON */}
                                    <Button
                                        className="w-100 mt-3"
                                        onClick={
                                            handleOtpSubmit
                                        }
                                    >
                                        Vérifier OTP
                                    </Button>


                                    {/* API ERROR */}
                                    {apiError && (

                                        <Alert
                                            variant="danger"
                                            className="mt-3 text-center"
                                        >
                                            {apiError}
                                        </Alert>
                                    )}


                                    {/* RESEND */}
                                    <div className="text-center mt-2">

                                        {canResend ? (

                                            <Button
                                                variant="link"
                                                onClick={
                                                    handleResendOtp
                                                }
                                            >
                                                Renvoyer OTP
                                            </Button>

                                        ) : (

                                            <span>
                                                Renvoyer à {timer}s
                                            </span>
                                        )}

                                    </div>


                                    {/* BACK */}
                                    <Button
                                        variant="link"
                                        className="back_button"
                                        onClick={
                                            handleBackToLogin
                                        }
                                    >
                                        ← Retour à la connexion
                                    </Button>

                                </>
                            )}


                            {/* FOOTER */}
                            <div className="text-center mt-auto">
                                © Sym Tech - 2026
                            </div>

                        </Stack>

                    </div>

                </Col>

            </Row>


            <LoadingComponent loading={loading} />

        </Container>
    );
};

export default Login;